import { SpinnerUntil } from "@edgetier/components";
import { CSSProperties, useCallback, useEffect, useMemo, useRef } from "react";
import { usePrevious } from "react-use";
import { IProps } from "./select-selected-list.types";
import { Virtuoso, VirtuosoHandle } from "react-virtuoso";
import styles from "../select-menu.module.scss";
import { Text } from "../../../text";
import { Menu } from "../../../menu";
import { Group } from "../../../group";
import { Accordion } from "../../../accordion";
import { Icon } from "../../../icon";
import { calculateGroupedFlatItemIndex } from "../select-menu.utilitites";
import { kebabCase } from "lodash";
import {
    DEFAULT_ITEM_ICON_COLOUR,
    ENTER_SELECTED_LIST_KEY,
    REMOVE_SELECTED_KEYS,
    SELECTED_ITEM_SELECTOR,
} from "../../select.constants";

/**
 * List of selected items that automatically scrolls to the bottom as each are added.
 * @param props.getGroup             Get a group name for an item.
 * @param props.getLabel             Get a label for an item.
 * @param props.getSelectedItemProps Function to get Downshift props for each item.
 * @param props.getValue             Get a value for an item.
 * @param props.removeSelectedItem   Method to remove an item.
 * @param props.selectedItems        Items chosen by the user.
 * @param props.itemLimit            Optional Maximum number of items that can be selected.
 * @param props.virtualized          Boolean that checks whether the list should be virtualized.
 * @param props.getGroupIcon         Function to get an icon for a group.
 * @param props.getIcon              Getter for an item's icon.
 * @param props.getIconProps         Getter for extra props for an item's icon, e.g. its colour.
 * @returns                          Scrollable list of items.
 */
const SelectSelectedList = <IItem extends {}, IValue extends {}>({
    children,
    getLabel,
    getSelectedItemProps,
    getGroup,
    getValue,
    isLoading,
    removeSelectedItem,
    selectedItems: items,
    setActiveIndex,
    virtualized,
    getGroupIcon,
    getIcon,
    getIconProps,
}: IProps<IItem, IValue>) => {
    const element = useRef<HTMLDivElement>(null);

    // Record the count of the selected items to decide whether scrolling needs to happen or not.
    const previousCount = usePrevious(items.length);

    const selectedItems = useMemo(
        () =>
            items.sort((one, two) =>
                // Now they will be sorted by group if any
                typeof getGroup === "function" ? getGroup(one).localeCompare(getGroup(two)) : 0
            ),
        [getGroup, items]
    );

    const { flatItems, flatItemIndexMap, groupedItemsWithIndex } = useMemo(
        () => calculateGroupedFlatItemIndex(selectedItems, getGroup),
        [selectedItems, getGroup]
    );

    // Scroll down when new items are added.
    useEffect(() => {
        if (element.current && typeof previousCount === "number" && items.length > previousCount) {
            element.current.scrollTop = element.current?.scrollHeight;
        }
    }, [items.length, previousCount]);

    // The rows on the page. A virtualised list only builds the rows on screen, so this is read fresh each time rather
    // than being counted from the items.
    const getRows = useCallback(
        () => Array.from(element.current?.querySelectorAll<HTMLElement>(SELECTED_ITEM_SELECTOR) ?? []),
        []
    );

    const virtuosoRef = useRef<VirtuosoHandle>(null);

    /**
     * Focus the row of an item, if it is on the page.
     * @param index The item's index in the list.
     * @returns     Whether the row was there to focus.
     */
    const focusRenderedItem = useCallback((index: number) => {
        const row = element.current?.querySelector<HTMLElement>(`[data-selected-item-index="${index}"]`);
        row?.focus();
        return typeof row !== "undefined" && row !== null;
    }, []);

    /**
     * Move the keyboard to an item of a virtualised list. The list only renders the rows on screen, so when the item's
     * row is not one of them the list is scrolled to it, only as far as its nearest edge, and the row is focused once
     * it is on the page. The list reports the scroll as done before rendering the rows, so that waits a frame.
     * @param index The item's index in the list.
     */
    const focusItem = useCallback(
        (index: number) => {
            if (!focusRenderedItem(index)) {
                virtuosoRef.current?.scrollIntoView({
                    index,
                    done: () => requestAnimationFrame(() => focusRenderedItem(index)),
                });
            }
        },
        [focusRenderedItem]
    );

    // Where to put the keyboard once a row it was standing on has gone, or null when it was somewhere else. That is the
    // item's index in a virtualised list, which only renders the rows on screen, and the row's position otherwise.
    const positionToRestore = useRef<number | null>(null);

    /**
     * Note that a row is being removed, so that the keyboard can be put back where it was standing.
     */
    const rememberPosition = useCallback(() => {
        const active = document.activeElement as HTMLElement;
        const position = getRows().indexOf(active);

        positionToRestore.current =
            position === -1 ? null : virtualized ? Number(active.dataset.selectedItemIndex) : position;
    }, [getRows, virtualized]);

    // Removing the row the keyboard was on leaves it standing on nothing, so it is put back at the same position,
    // or at the end if that was the last row. This waits a frame because a virtualised list rebuilds its rows after
    // the removal rather than during it, and the row focused before then is thrown away with the old ones.
    useEffect(() => {
        if (positionToRestore.current === null) {
            return;
        }

        const position = positionToRestore.current;

        positionToRestore.current = null;

        const frame = requestAnimationFrame(() => {
            const rows = getRows();
            const count = virtualized ? items.length : rows.length;
            const destination = Math.min(position, count - 1);

            if (count === 0) {
                setActiveIndex(-1);
                return;
            }

            setActiveIndex(destination);
            if (virtualized) {
                focusItem(destination);
            } else {
                rows[destination]?.focus();
            }
        });

        return () => cancelAnimationFrame(frame);
    }, [focusItem, getRows, items.length, setActiveIndex, virtualized]);

    /**
     * Answer every key that moves around this column, so that the whole of its keyboard behaviour is described here.
     *
     * The menu gives each of its items a key handler that walks all of them, which in this menu means walking out of
     * this column and into the options beside it. That handler runs on the way back up and ignores `preventDefault`,
     * so this one listens on the way down and stops the keys before they reach it.
     * @param event The keyboard event from somewhere in the column.
     */
    const onKeyDownCapture = useCallback(
        (event: React.KeyboardEvent) => {
            const rows = getRows();
            const position = rows.findIndex((row) => row.contains(event.target as Node));

            if (position === -1) {
                return;
            }

            // A virtualised list only renders the rows on screen, so its keyboard walks the items instead.
            const current = virtualized ? Number(rows[position].dataset.selectedItemIndex) : position;
            const count = virtualized ? items.length : rows.length;

            // Removing is exactly what clicking the row does, so these keys are handed to it rather than growing a
            // second way to remove the same thing.
            if (REMOVE_SELECTED_KEYS.includes(event.key)) {
                event.preventDefault();
                event.stopPropagation();

                return rows[position].click();
            }

            // Where each key leaves the keyboard: -1 is the search box, to the left of this column. The right arrow
            // stays where it is, because nothing sits further right, but it is still answered so that the menu
            // cannot walk it into the options.
            const destinations: Record<string, number> = {
                ArrowDown: (current + 1) % count,
                ArrowUp: (current - 1 + count) % count,
                ArrowLeft: -1,
                [ENTER_SELECTED_LIST_KEY]: current,
                Home: 0,
                End: count - 1,
            };

            if (!(event.key in destinations)) {
                return;
            }

            event.preventDefault();
            event.stopPropagation();

            const destination = destinations[event.key];

            setActiveIndex(destination);

            // Downshift moves the focus itself, but only to rows that are on the page, and a virtualised list only
            // renders the rows on screen. The row is focused here instead, which is how the search box hands the
            // keyboard over too.
            if (virtualized && destination !== -1) {
                focusItem(destination);
            } else {
                rows[destination]?.focus();
            }
        },
        [focusItem, getRows, items.length, setActiveIndex, virtualized]
    );

    const renderItem = useCallback(
        (key: string, style: CSSProperties, index: number, selectedItem: IItem) => {
            return (
                <Menu.Item
                    {...getSelectedItemProps({ selectedItem, index })}
                    onClick={() => {
                        rememberPosition();
                        removeSelectedItem(selectedItem);
                    }}
                    key={key}
                    style={style}
                    aria-label={getLabel(selectedItem)}
                    className={styles["select-menu__option"]}
                    data-testid={`selected-item-${kebabCase(getLabel(selectedItem))}`}
                    data-selected-item
                    data-selected-item-index={index}
                >
                    <Group w="100%" align="center" wrap="nowrap" gap="sm">
                        {typeof getIcon === "function" && (
                            <Icon
                                icon={getIcon(selectedItem)}
                                color={DEFAULT_ITEM_ICON_COLOUR}
                                {...getIconProps?.(selectedItem)}
                            />
                        )}
                        <Text
                            size="sm"
                            className={styles["select-menu__option__label"]}
                            data-testid={`selected-item-label-${kebabCase(getLabel(selectedItem))}`}
                        >
                            {typeof children === "function" ? children(selectedItem) : getLabel(selectedItem)}
                        </Text>
                    </Group>
                </Menu.Item>
            );
        },
        [getSelectedItemProps, getLabel, children, rememberPosition, removeSelectedItem, getIcon, getIconProps]
    );

    return (
        <div className={`${styles["select-menu__list"]} ${styles["select-menu__list--is-selected"]}`}>
            <div className={styles["select-menu__options"]} ref={element} onKeyDownCapture={onKeyDownCapture}>
                <SpinnerUntil data={[]} isReady={!isLoading}>
                    {virtualized ? (
                        <ul data-testid="selected-items">
                            <Virtuoso
                                ref={virtuosoRef}
                                data={selectedItems}
                                itemContent={(index) => {
                                    const selectedItem = selectedItems[index];
                                    const value = getValue(selectedItem);
                                    const key =
                                        typeof value === "object" ? Object.values(value).join() : value.toString();

                                    return renderItem(key, {}, index, selectedItem);
                                }}
                            />
                        </ul>
                    ) : (
                        <ul data-testid="selected-items">
                            {typeof getGroup === "function" ? (
                                <Accordion>
                                    {Object.entries(groupedItemsWithIndex).map(([group, items]) => (
                                        <Accordion.Item key={group} value={group} bd="none">
                                            <Accordion.Control
                                                className={styles["select-menu__accordion__control"]}
                                                icon={getGroupIcon?.(group) ?? undefined}
                                                data-testid={`selected-items-group-${kebabCase(group)}`}
                                            >
                                                <Text
                                                    span
                                                    size="sm"
                                                    data-testid={`selected-items-group-label-${kebabCase(group)}`}
                                                >
                                                    {group}
                                                </Text>
                                            </Accordion.Control>
                                            <Accordion.Panel pl="xxl">
                                                {items.map(({ item: selectedItem }, index) => {
                                                    const value = getValue(selectedItem);
                                                    const key =
                                                        typeof value === "object"
                                                            ? Object.values(value).join()
                                                            : value.toString();
                                                    const flatItemIndex = flatItemIndexMap.get(selectedItem);

                                                    return renderItem(key, {}, flatItemIndex ?? index, selectedItem);
                                                })}
                                            </Accordion.Panel>
                                        </Accordion.Item>
                                    ))}
                                </Accordion>
                            ) : (
                                selectedItems.map((selectedItem, index) => {
                                    const value = getValue(selectedItem);
                                    const key =
                                        typeof value === "object" ? Object.values(value).join() : value.toString();

                                    return renderItem(key, {}, index, selectedItem);
                                })
                            )}
                        </ul>
                    )}
                </SpinnerUntil>
            </div>
        </div>
    );
};

export default SelectSelectedList;
